import { collection, doc, onSnapshot, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { firestore, functions } from '../firebase';
import type { RepairRequest } from '../types/firestore';
import type { ReviewInvitation } from '../types/reviewInvitation';

export interface ReviewInvitationContext {
  request: RepairRequest | null;
  canonicalReviewExists: boolean;
}

type SnoozeReviewInvitationResponse = {
  success: boolean;
  changed: boolean;
  requestId: string;
  status: 'pending';
  snoozedUntilMs: number;
};

export interface SnoozeReviewInvitationInput {
  requestId: string;
}

export const subscribeToOwnPendingReviewInvitations = (
  clientId: string,
  onNext: (invitations: ReviewInvitation[]) => void,
  onError?: (error: Error) => void
): (() => void) => {
  const normalizedClientId = String(clientId || '').trim();
  if (!normalizedClientId) {
    onNext([]);
    return () => undefined;
  }

  const invitationsQuery = query(
    collection(firestore, 'reviewInvitations'),
    where('clientId', '==', normalizedClientId),
    where('schemaVersion', '==', 2),
    where('status', '==', 'pending')
  );

  return onSnapshot(
    invitationsQuery,
    (snapshot) => {
      onNext(
        snapshot.docs.map((snapshotDoc) => ({
          ...snapshotDoc.data(),
          id: snapshotDoc.id,
        }))
      );
    },
    (error) => {
      console.error('Error subscribing to pending review invitations:', error);
      onError?.(error as Error);
    }
  );
};

export const subscribeToReviewInvitationContext = (
  requestId: string,
  onNext: (context: ReviewInvitationContext) => void,
  onError?: (error: Error) => void
): (() => void) => {
  const normalizedRequestId = String(requestId || '').trim();
  if (!normalizedRequestId) {
    return () => undefined;
  }

  const requestRef = doc(firestore, 'repairRequests', normalizedRequestId);
  const reviewRef = doc(firestore, 'reviews', normalizedRequestId);
  let request: RepairRequest | null = null;
  let canonicalReviewExists = false;
  let requestReady = false;
  let reviewReady = false;

  const emit = () => {
    if (!requestReady || !reviewReady) return;
    onNext({ request, canonicalReviewExists });
  };

  const unsubscribeRequest = onSnapshot(
    requestRef,
    (snapshot) => {
      request = snapshot.exists()
        ? ({ uid: snapshot.id, id: snapshot.id, ...snapshot.data() } as RepairRequest)
        : null;
      requestReady = true;
      emit();
    },
    (error) => {
      console.error('Error subscribing to review invitation request:', error);
      onError?.(error as Error);
    }
  );

  const unsubscribeReview = onSnapshot(
    reviewRef,
    (snapshot) => {
      canonicalReviewExists = snapshot.exists();
      reviewReady = true;
      emit();
    },
    (error) => {
      console.error('Error subscribing to canonical review:', error);
      onError?.(error as Error);
    }
  );

  return () => {
    unsubscribeRequest();
    unsubscribeReview();
  };
};

export const snoozeReviewInvitation = async ({
  requestId,
}: SnoozeReviewInvitationInput): Promise<void> => {
  const normalizedRequestId = String(requestId || '').trim();
  if (!normalizedRequestId) {
    throw new Error('Nieprawidłowe ID zaproszenia do opinii.');
  }

  const snoozeFn = httpsCallable<{ requestId: string }, SnoozeReviewInvitationResponse>(
    functions,
    'snoozeReviewInvitation'
  );
  const result = await snoozeFn({ requestId: normalizedRequestId });
  if (result.data?.success !== true || result.data.requestId !== normalizedRequestId) {
    throw new Error('Nie udało się odroczyć zaproszenia do opinii.');
  }
};

export const reviewInvitationService = {
  subscribeToOwnPendingReviewInvitations,
  subscribeToReviewInvitationContext,
  snoozeReviewInvitation,
};
